import {
  ChangeEvent,
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  useRef,
} from 'react';

// React 17 ships without `useId`; a module-level counter gives us stable
// per-instance IDs for the aria-describedby anchor. Each component grabs
// its index on first render and keeps it across re-renders via useRef.
let selectableCardIdCounter = 0;

export type SelectableCardVariant = 'single' | 'multiple';

interface OwnProps {
  /**
   * `single` renders a radio-style affordance (round box with a filled
   * inner dot). Use for "pick one of N" pickers grouped by `name`.
   *
   * `multiple` renders a checkbox-style affordance (square box with a
   * tick). Use for "tick zero-or-more" pickers — including the
   * legacy "use collection address" billing-option pattern.
   */
  variant: SelectableCardVariant;
  /**
   * Visible label / heading content. String or rich JSX.
   */
  label: ReactNode;
  /**
   * Optional secondary line(s) shown beneath the label. Renders inside
   * an aria-describedby anchor so screen readers announce it together
   * with the label when the affordance is focused.
   */
  description?: ReactNode;
  /**
   * Optional inline supporting content rendered to the right of the
   * label block. Typical use: a price, a chip, or a meta numeral.
   */
  trailing?: ReactNode;
  /**
   * Optional icon rendered to the left of the label block. Mirror the
   * 20–24px sizing the rest of the design language uses.
   */
  leadingIcon?: ReactNode;
  /**
   * Controlled checked state. SelectableCard is always controlled —
   * pair with `onChange` to flip it.
   */
  checked: boolean;
  /**
   * Change handler. Receives the new boolean state and the underlying
   * native ChangeEvent so consumers can still hit `target.value` etc.
   */
  onChange: (next: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * `name` on the underlying input. Required when wiring radio groups
   * (`variant='single'`) so the browser scopes the selection to the group.
   */
  name?: string;
  /**
   * className appended to the wrapping `<label>` element.
   */
  className?: string;
}

export type SelectableCardProps = OwnProps &
  Omit<
    InputHTMLAttributes<HTMLInputElement>,
    keyof OwnProps | 'type' | 'onChange' | 'checked'
  >;

/**
 * Canonical card-style selectable primitive.
 *
 * Visual anatomy:
 *   ┌──────────────────────────────────────────────┐
 *   │ ( ● )  Label text              [trailing]    │
 *   │        Optional description                  │
 *   └──────────────────────────────────────────────┘
 *
 * - Entire card body is the click target (label wraps a hidden input).
 * - Min-height 44px so the tap-target rule is satisfied even when the
 *   label is a single short word.
 * - `single` variant paints a radio (round indicator + inner dot when
 *   checked); `multiple` paints a checkbox (square indicator + tick).
 * - Focus-visible ring sits on the WRAPPER via `:focus-within`, mirroring
 *   the `<Button>` and `<Checkbox>` focus recipe — 2px brand outline
 *   at 2px offset.
 * - Hover drops opacity to 0.96 + nudges the border colour. Honours
 *   `prefers-reduced-motion`.
 *
 * Mirrors the `<Checkbox>` paint recipe — the SCSS partial reuses the
 * same purple-focus + filled-active treatment so the two primitives
 * read as a family.
 *
 * Usage:
 *   ```tsx
 *   <SelectableCard
 *     variant="multiple"
 *     label="Use collection address"
 *     description="Recommended for international cards"
 *     checked={billingMatches}
 *     onChange={(next) => setBillingMatches(next)}
 *   />
 *   ```
 */
export const SelectableCard = forwardRef<HTMLInputElement, SelectableCardProps>(
  function SelectableCard(props, ref) {
    const {
      variant,
      label,
      description,
      trailing,
      leadingIcon,
      checked,
      onChange,
      name,
      className,
      id,
      disabled,
      ...rest
    } = props;

    // Stable per-instance ID for the aria-describedby anchor. useRef
    // captures the counter value on first render so the ID stays put
    // across re-renders even when many SelectableCards share a viewport
    // (e.g. a radio group rendered from `.map`).
    const idRef = useRef<number | null>(null);
    if (idRef.current === null) {
      selectableCardIdCounter += 1;
      idRef.current = selectableCardIdCounter;
    }
    const descriptionId = description
      ? `selectable-card-${idRef.current}-desc`
      : undefined;

    const wrapperClass = [
      'selectable-card',
      `selectable-card--${variant}`,
      checked ? 'selectable-card--checked' : null,
      disabled ? 'selectable-card--disabled' : null,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
      onChange(event.target.checked, event);
    };

    return (
      <label className={wrapperClass} htmlFor={id}>
        <input
          {...rest}
          ref={ref}
          id={id}
          type={variant === 'single' ? 'radio' : 'checkbox'}
          name={name}
          className="selectable-card__input"
          checked={checked}
          disabled={disabled}
          onChange={handleChange}
          aria-describedby={descriptionId}
        />
        <span className="selectable-card__indicator" aria-hidden="true">
          {variant === 'multiple' ? (
            <svg
              className="selectable-card__tick"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.5 8.25L6.5 11.25L12.5 5.25"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <span className="selectable-card__dot" />
          )}
        </span>
        {leadingIcon && (
          <span className="selectable-card__leading" aria-hidden="true">
            {leadingIcon}
          </span>
        )}
        <span className="selectable-card__content">
          <span className="selectable-card__label">{label}</span>
          {description && (
            <span
              id={descriptionId}
              className="selectable-card__description"
            >
              {description}
            </span>
          )}
        </span>
        {trailing && (
          <span className="selectable-card__trailing">{trailing}</span>
        )}
      </label>
    );
  },
);
